import { css } from '@emotion/react';
import { Divider, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { Suspense, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { filterRestrictedColumns } from '../../../components/ColumnManagement/utils';
import { PermissionGatedWrapper } from '../../../components/Fallback/PermissionGatedWrapper';
import { FormFactoryNoLayout } from '../../../components/Form/FormFactory';
import { RendererType } from '../../../data-models/field-definition';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { companyFields } from '../../../schemas/Company.schema';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { FDMap } from '../../../util/data-structure/FDMap';
import { NumericFormattersConfig } from '../../../util/formatters/NumericFormatters';
import { useSchemaToFormFields } from '../../../util/schema-utils';
import {
  createForm,
  IFormField,
  IFormFieldSelectMeta,
  IFormFieldTextMeta,
} from '../../../view-models/form.view-model';
import { selectedColumnsGridDisplayFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { selectedCompanyIdProfileAtom } from '../../CompanyProfiles/state/UIStateJ';
import { columnNameToPathState } from '../../PortfolioOverview/state/ViewStateJ';
import { colors } from '../../../theme/colors';
import { cardStyles } from '../../../theme/component-styles';
import { useShowSummaryV2 } from './useShowSummaryV2';
import { MUIAvatar } from '../../../components/Avatar/MUIAvatar';
import { MUIAvatarGroup } from '../../../components/Avatar/MUIAvatarGroup';
import { Label } from '../../../components/Form/FormComponents';
import { EmailButton } from '../../CompanyProfiles/Summary/components/People/EmailButton';
import { SectionTitle } from '../../CompanyProfiles/Summary/components/SubSection';
import { usePeopleBreakdown } from '../utils/usePeopleBreakdown';
import { FieldsWrapper, usePanelPagination } from './CompanyDetailsPanel';
import { Panel } from './Panel';
import { PrimaryForbiddenMessage } from './constants/forbidden-messages.constants';
import { fromCompanyDataModel, IDigestPanelViewModel, toCompanyDataModel } from './DigestPanel.view-model';
import { useDebounceCompanyUpdates } from './useDebounceCompanyUpdates';
import { useCompanyEditPerms } from './useEditPerms';
import { withDashPlaceholder } from './withDashPlaceholder';
import { useMergedDigestData } from './useMergedDigestData';

// The Digest's own card. Not `summaryCardCss` — that slot is chrome-less because its section puts
// the border on the collapsible body; the Digest has no body to collapse, so it carries the border.
const digestCardCss = css({
  ...cardStyles,
  display: 'grid',
  gap: '0.75rem',
  gridTemplateRows: 'min-content 1fr',
  padding: '0.75rem',
});

const DigestContents = styled('div')`
  display: grid;
  height: 100%;
  grid-template-rows: max-content 1fr;
  & [data-fieldkey='shortDescription'],
  & [data-fieldkey='description'] {
    grid-column: 1/-1;
  }
`;

/**
 * The V2 Digest lists one field per row with its label beside the value — not the two columns of
 * label-over-value that the shared `FieldsWrapper` draws. Local to this card, as `FieldsWrapper` is
 * shared with Company Details, both Fund Profiles panels and three Deal Drawer panels.
 *
 * The 126px label track is the design's. `FormField` stacks label over value via
 * `grid-template-rows`, so that has to be flattened back to a single row here.
 */
const digestFieldsV2Css = css`
  align-content: start;
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr);

  & [data-fieldkey] {
    align-items: center;
    grid-template-columns: 126px minmax(0, 1fr);
    grid-template-rows: auto;
  }

  /* The descriptions are full-width prose in the design, so they keep the stacked arrangement. */
  & [data-fieldkey='shortDescription'],
  & [data-fieldkey='description'] {
    grid-template-columns: minmax(0, 1fr);
  }

  /* The label ellipsizes into the value column, as the design draws long labels. \`LabelText\` is
     display: flex, on which text-overflow cannot act. */
  & .field-label label.MuiTypography-root {
    display: block;
  }

  /* Values are body2 (14px), not the input default (16px), and the rows are tighter than the
     inputs' resting padding. */
  & .MuiInputBase-root {
    font-size: 0.875rem;
    letter-spacing: 0.17px;
  }
  & .MuiInputBase-input {
    padding-top: 2px;
    padding-bottom: 2px;
  }

  /* One line per value, as the design draws these rows: the chips and the ghost text input stay on
     one row, with the first chip truncating rather than pushing the "+N" chip and the icons onto a
     second line. The ghost input gives up its 30px floor at rest and takes it back on focus, so
     there is always room to click into the field and type.

     \`&&\` throughout: MUI's own slot styles land at the same specificity as a single-class scope
     (e.g. \`flex-wrap: wrap\` for multiple autocompletes), leaving the winner to injection order.
     Doubling the scope class settles it by specificity instead. */
  && .MuiAutocomplete-inputRoot {
    flex-wrap: nowrap;
  }
  && .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
    min-width: 0;
  }
  && .MuiAutocomplete-inputRoot:focus-within .MuiAutocomplete-input {
    min-width: 30px;
  }

  /* The hover controls (clear, popup) are absolutely positioned in the input's right padding; own
     that padding here so the truncated value can never extend underneath them. */
  && .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot {
    padding-right: 2rem;
  }
  && .MuiAutocomplete-hasClearIcon.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot {
    padding-right: 3.5rem;
  }

  /* The design draws multi values as plain text like their single-select neighbours, not as pills —
     the pill is just MUI's default for multiple autocompletes. (The "+N" counter is a styled div,
     not a Chip, so these rules cannot touch it.) */
  & .MuiChip-root {
    background: transparent;
    /* Flex refuses to shrink an item below its content width by default; without this the chip
       overflows the single line instead of truncating on it. */
    min-width: 0;
  }
  & .MuiChip-root .MuiChip-label {
    font-size: 0.875rem;
    letter-spacing: 0.17px;
    padding: 0;
  }

  /* The design's "+N" counter: purple caption in a bordered square. \`MoreChip\`'s base style is the
     company cards' chip, which these rows restyle rather than fork. #d5d7eb is Figma's
     \`border/default/tooltip\`, which has no token in \`colors\`. */
  & [data-testid='morechip'] {
    border-color: #d5d7eb;
    color: ${colors.secondary[50]};
    flex-shrink: 0;
    font-size: 12px;
    margin: 0 0.25rem;
  }
`;

/** The Strongest Connection row, drawn to the same label/value tracks as the form rows above it. */
const strongestConnectionRowCss = css`
  align-items: center;
  display: grid;
  grid-template-columns: 126px minmax(0, 1fr);
`;

/** Per design the avatar stack sits at the row's right edge, unlike the left-aligned field values. */
const strongestConnectionValueCss = css`
  justify-self: end;
`;

export function DigestPanel({ companyId }: { companyId: number }) {
  const showSummaryV2 = useShowSummaryV2();
  const viewModel = useDigestPaneViewModel();
  const digestPanelSchema = getDigestPanelSchema(viewModel);
  const schemaToFormFields = useSchemaToFormFields();
  const setEditableFields = useCompanyEditPerms(companyId);
  const columnsToHide = useAtomValue(selectedColumnsGridDisplayFieldsOrgState);
  const columnNameToPathMap = useAtomValue(columnNameToPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));

  const mainFieldsSchema = digestPanelSchema;
  const mainFields = useMemo(
    () => setEditableFields(schemaToFormFields(mainFieldsSchema)),
    [setEditableFields, schemaToFormFields, mainFieldsSchema]
  );

  const filterFields = useMemo(() => {
    return filterRestrictedColumns(
      mainFields.map((field) => field.key.replace('_viewModel.', '')),
      columnsToHide,
      columnNameToPathMap
    );
  }, [mainFields, columnsToHide, columnNameToPathMap]);

  const permittedFields = useMemo(
    () =>
      mainFields.filter((field) => {
        const normalizedKey = field.key.replace('_viewModel.', '');
        return filterFields.includes(normalizedKey);
      }),
    [mainFields, filterFields]
  );

  // The design puts a mail button after the CEO's name. Attached to the converted form field, NOT
  // to the yup schema's formMeta — yup deep-clones meta on every shape(), and React elements do not
  // survive its clone. Suspended independently so the people fetch cannot hold up the card.
  const v2Fields = useMemo(() => {
    if (!showSummaryV2) {
      return permittedFields;
    }
    return permittedFields.map((field) =>
      field.key === '_viewModel.ceoName'
        ? {
            ...field,
            rendererMeta: {
              ...(field.rendererMeta as IFormFieldSelectMeta<string>),
              endAdornment: (
                <Suspense fallback={null}>
                  <CeoEmailButton companyId={companyId} ceoNames={viewModel._viewModel.ceoName} />
                </Suspense>
              ),
            },
          }
        : field
    );
  }, [permittedFields, showSummaryV2, companyId, viewModel._viewModel.ceoName]);

  // V1 paginates at 9 fields per page. V2 shows every field in one list, so both pages are open —
  // the hook still runs either way, because hooks cannot be called conditionally.
  const { paginationConfig, visibleFields } = usePanelPagination({
    fields: permittedFields,
    itemsPerPage: 9,
  });

  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewCompanyBasics]}
      fallbackMessage={PrimaryForbiddenMessage}
    >
      {showSummaryV2 ? (
        // Deliberately not a SummarySection: per design the V2 Digest is a plain card that does not
        // collapse, so it has no chevron and its chrome wraps the whole card rather than a body.
        <div css={digestCardCss}>
          <SectionTitle title={'Digest'} />
          <DigestMainSection digestViewModel={viewModel} fields={withDashPlaceholder(v2Fields)} />
        </div>
      ) : (
        <Panel
          title={'Digest'}
          paginationConfig={paginationConfig}
          showContentOverflow={true}
          style={{ minHeight: '26rem' }}
        >
          <DigestMainSection digestViewModel={viewModel} fields={visibleFields} />
        </Panel>
      )}
    </PermissionGatedWrapper>
  );
}

interface IDigestMainSectionProps {
  fields: IFormField<unknown>[];
  digestViewModel: IDigestPanelViewModel;
}

function DigestMainSection(props: IDigestMainSectionProps) {
  const { digestViewModel, fields } = props;
  const showSummaryV2 = useShowSummaryV2();
  const companyId = digestViewModel.id;
  const methods = useForm({
    values: digestViewModel,
  });
  const onChange = useDebounceCompanyUpdates(companyId as number);

  const _onChange = useCallback(
    (data: Partial<IDigestPanelViewModel>) => {
      onChange(toCompanyDataModel({ ...data, id: companyId }));
    },
    [companyId, onChange]
  );

  const form = createForm<IDigestPanelViewModel>({
    fields: FDMap.fromArray(fields, 'key'),
    variant: 'form-inline',
  });

  // V2 splits the descriptions off so the Strongest Connection row can sit between them and the
  // fields, where the design draws it. Both halves share `methods`, so editing is unaffected.
  const descriptionForm = createForm<IDigestPanelViewModel>({
    fields: FDMap.fromArray(
      fields.filter((f) => DigestDescriptionKeys.has(f.key)),
      'key'
    ),
    variant: 'form-inline',
  });
  const mainForm = createForm<IDigestPanelViewModel>({
    fields: FDMap.fromArray(
      fields.filter((f) => !DigestDescriptionKeys.has(f.key)),
      'key'
    ),
    variant: 'form-inline',
  });

  return (
    <FormProvider {...methods}>
      <DigestContents>
        {showSummaryV2 ? (
          <div css={digestFieldsV2Css}>
            <FormFactoryNoLayout form={mainForm} onChange={_onChange} />
            <Divider />
            <Suspense fallback={null}>
              <StrongestConnectionRow companyId={companyId as number} />
            </Suspense>
            <Divider />
            <FormFactoryNoLayout form={descriptionForm} onChange={_onChange} />
          </div>
        ) : (
          <FieldsWrapper style={{ minHeight: '60%' }}>
            <FormFactoryNoLayout form={form} onChange={_onChange} />
          </FieldsWrapper>
        )}
      </DigestContents>
    </FormProvider>
  );
}

const DigestDescriptionKeys = new Set(['shortDescription', 'description']);

/**
 * The design's "Strongest Connection" digest row: the top-scored relationships as an avatar stack.
 * Same data as the Discovery layout's Strongest Connections card (`usePeopleBreakdown`).
 */
function StrongestConnectionRow({ companyId }: { companyId: number }) {
  const { getRelationships } = usePeopleBreakdown(companyId);
  const relationships = useMemo(() => getRelationships(), [getRelationships]);

  return (
    <div css={strongestConnectionRowCss} data-testid='strongest-connection-row'>
      <Label>Strongest Connection</Label>
      {relationships.length === 0 ? (
        <Typography variant='body2' color='text.secondary' css={strongestConnectionValueCss}>
          -
        </Typography>
      ) : (
        <div css={strongestConnectionValueCss}>
          <MUIAvatarGroup size='small' max={3}>
            {relationships.map((person) => (
              <MUIAvatar
                key={person.id}
                nameParts={[person.firstName ?? '', person.lastName ?? '']}
                src={person.logoUrl}
                size='small'
                title={person.name}
              />
            ))}
          </MUIAvatarGroup>
        </div>
      )}
    </div>
  );
}

/**
 * Mail button for the CEO named in the digest, resolved to the actual person record so the popover
 * carries their real address (`EmailButton` falls back to the secondary source when it is missing).
 */
function CeoEmailButton({ companyId, ceoNames }: { companyId: number; ceoNames: (string | undefined)[] }) {
  const { visiblePeople } = usePeopleBreakdown(companyId);
  const names = ceoNames.filter(Boolean).map((name) => name!.trim().toLowerCase());
  const person = visiblePeople?.find((p) => {
    const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ').trim().toLowerCase();
    return names.includes(p.name.trim().toLowerCase()) || (!!fullName && names.includes(fullName));
  });

  if (!person) {
    return null;
  }
  return (
    <EmailButton
      companyId={companyId}
      personId={person.id}
      primaryEmail={person.primaryEmail}
      secondaryEmails={person.secondaryEmails}
    />
  );
}

export function useDigestPaneViewModel() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId))!;
  const data = useMergedDigestData(company);

  return fromCompanyDataModel(data);
}

export function getDigestPanelSchema(viewModel: IDigestPanelViewModel) {
  const ceoNameOptions: ISimpleChoice<string>[] = viewModel._viewModel.ceoName.map((name) => {
    return { displayName: name, value: name };
  });
  const aliasesOptions: ISimpleChoice<string>[] =
    viewModel.aliases?.map((alias) => {
      return { displayName: alias, value: alias };
    }) ?? [];
  const latestLeadInvestorOptions: ISimpleChoice<string>[] =
    viewModel.latestLeadInvestor?.map((investor) => {
      return { displayName: investor, value: investor };
    }) ?? [];

  const {
    aliases,
    description,
    foundedDate,
    lastPostMoney,
    latestFundingDate,
    latestLeadInvestor,
    shortDescription,
    socials,
  } = companyFields();

  return yup.object({
    foundedDate,
    ceoName: yup
      .array()
      .of(yup.string())
      .formMeta<IFormFieldSelectMeta<string>>({
        key: '_viewModel.ceoName',
        label: 'CEO Names',
        formatter: 'stringArray',
        renderer: RendererType.multiSelect,
        rendererMeta: {
          allowCustomAdd: true,
          // This card sits in the 340px rail, narrow enough to clip long options.
          inlinePopper: true,
          values: ceoNameOptions,
        },
      }),
    lastPostMoney: lastPostMoney.formMeta<IFormFieldTextMeta>({
      disabled: true,
      formatter: NumericFormattersConfig.usdShort,
    }),
    primaryLocation: yup
      .string()
      .nullable()
      .default(null)
      .formMeta<IFormFieldSelectMeta<unknown>>({
        key: '_viewModel.primaryLocation',
        renderer: RendererType.location,
        rendererMeta: {
          inlinePopper: true,
          multi: false,
          values: [],
          withLocationIcon: true,
        },
      }),
    lastFunding: yup.string().formMeta({
      key: '_viewModel.lastFunding',
      disabled: true,
    }),
    secondaryLocations: yup
      .array()
      .of(yup.mixed())
      .label('Secondary Locations')
      .default([])
      .formMeta<IFormFieldSelectMeta<unknown>>({
        key: '_viewModel.secondaryLocations',
        renderer: RendererType.location,
        rendererMeta: {
          inlinePopper: true,
          multi: true,
          values: [],
          withLocationIcon: true,
        },
      }),
    latestFundingDate: latestFundingDate.formMeta<IFormFieldTextMeta>({
      disabled: true,
    }),
    aliases: aliases.formMeta({
      key: 'aliases',
      renderer: RendererType.multiSelect,
      formatter: 'stringArray',
      rendererMeta: {
        allowCustomAdd: true,
        inlinePopper: true,
        values: aliasesOptions,
      },
    }),
    shortDescription: shortDescription.label('Short Description').formMeta<IFormFieldTextMeta>({
      renderer: RendererType.text,
      variant: 'form-inline',
      rendererMeta: {
        multiline: true,
        maxRows: 2,
      },
    }),
    socials: socials.formMeta({
      renderer: RendererType.companySocialMedia,
    }),
    latestLeadInvestor: latestLeadInvestor.formMeta({
      key: 'latestLeadInvestor',
      disabled: true,
      renderer: RendererType.multiSelect,
      formatter: 'stringArray',
      rendererMeta: {
        allowCustomAdd: true,
        inlinePopper: true,
        values: latestLeadInvestorOptions,
      },
    }),
    description: description.formMeta<IFormFieldTextMeta>({
      renderer: RendererType.text,
      rendererMeta: {
        maxRows: 3,
        multiline: true,
      },
      variant: 'form-inline',
    }),
  });
}
